import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "api-store/store";
import { CircularProgress } from "@mui/material";
import {
  SELF_SERVICE_CATALOG_LIST as STATIC_CATALOG_LIST,
  SELF_SERVICE_PAGE_CONSTANTS,
  CLOUD_SERVICE_PAGE_CONSTANTS,
} from "utility/constants";
import {
  PageDescription,
  PageTitle,
  PageTopSection,
  PageWrapper,
} from "utility/styled-components";
import {
  SelfSerivceCatalogDescription,
  SelfSerivceCatalogDown,
  SelfServiceCatalogFooter,
  SelfSerivceCatalogGroup,
  SelfSerivceCatalogGroupItem,
  SelfSerivceCatalogGroupItems,
  SelfSerivceCatalogGroupTitle,
  SelfSerivceCatalogIconContainer,
  SelfSerivceCatalogIconWrapper,
  SelfSerivceCatalogPreRequisite,
  SelfSerivceCatalogPreRequisites,
  SelfSerivceCatalogPreRequisiteTitle,
  SelfSerivceCatalogTitle,
  SelfSerivceCatalogTop,
  SelfSerivceCatalogWrapper,
  SelfServiceCatalogFooterLeft,
  SelfServiceCatalogFooterLink,
} from "./style";
import { PrimaryButton } from "pages/overview-page/styles";
import { Link } from "react-router-dom";
import { fetchCloudServicesRequest } from "pages/cloud-service-provisioning/ducks/slice";
import {
  getCloudServicesData,
  getCloudServicesLoading,
  getCloudServicesFetchAttempted,
} from "pages/cloud-service-provisioning/ducks/selector";
import { LoaderContainer, LoaderText } from "utility/styles";
import CloudServiceImage from "assets/cloudService";

const CloudServiceLoader = () => (
  <LoaderContainer>
    <CircularProgress size={32} sx={{ color: "#EB0A1E" }} />
    <LoaderText>Loading cloud services...</LoaderText>
  </LoaderContainer>
);

function SelfServicePortal() {
  const dispatch = useDispatch<AppDispatch>();
  const [catalogList, setCatalogList] = useState(STATIC_CATALOG_LIST);

  const cloudServicesData = useSelector((state: RootState) =>
    getCloudServicesData(state)
  );
  const isLoadingCloudServices = useSelector((state: RootState) =>
    getCloudServicesLoading(state)
  );
  const fetchAttempted = useSelector((state: RootState) =>
    getCloudServicesFetchAttempted(state)
  );

  useEffect(() => {
    if (!fetchAttempted && !isLoadingCloudServices) {
      dispatch(fetchCloudServicesRequest());
    }
  }, [dispatch, fetchAttempted, isLoadingCloudServices]);

  useEffect(() => {
    if (Array.isArray(cloudServicesData) && cloudServicesData.length > 0) {
      setCatalogList((prevData) =>
        prevData.map((obj) =>
          obj.title === CLOUD_SERVICE_PAGE_CONSTANTS.title
            ? { ...obj, items: cloudServicesData }
            : obj
        )
      );
    }
  }, [cloudServicesData, fetchAttempted, isLoadingCloudServices]);

  const getPathnameForCatalogItem = (
    catalogGroupTitle: string,
    selectEndpoint: string
  ) => {
    if (catalogGroupTitle === CLOUD_SERVICE_PAGE_CONSTANTS.title) {
      return "cloud-services/" + selectEndpoint;
    }
    return selectEndpoint;
  };

  return (
    <PageWrapper>
      <PageTopSection>
        <PageTitle>{SELF_SERVICE_PAGE_CONSTANTS.title}</PageTitle>
        <PageDescription>
          {SELF_SERVICE_PAGE_CONSTANTS.description}
        </PageDescription>
      </PageTopSection>
      <SelfSerivceCatalogWrapper>
        {catalogList.map((catalogGroup, index) => (
          <SelfSerivceCatalogGroup key={index + 1}>
            <SelfSerivceCatalogGroupTitle>
              <span></span>
              <span>{catalogGroup.title}</span>
            </SelfSerivceCatalogGroupTitle>
            {catalogGroup.title === CLOUD_SERVICE_PAGE_CONSTANTS.title &&
            isLoadingCloudServices ? (
              <CloudServiceLoader />
            ) : null}
            <SelfSerivceCatalogGroupItems>
              {catalogGroup.items.map((catalogItem, catalogItemIndex) => {
                const CardImage =
                  catalogGroup.title === CLOUD_SERVICE_PAGE_CONSTANTS.title
                    ? CloudServiceImage
                    : catalogItem.CardImage;
                return (
                  <SelfSerivceCatalogGroupItem key={catalogItemIndex + 1}>
                    <SelfSerivceCatalogTop>
                      <SelfSerivceCatalogIconWrapper>
                        <SelfSerivceCatalogIconContainer>
                          {CardImage ? (
                            <CardImage width={13} height={13} />
                          ) : null}
                        </SelfSerivceCatalogIconContainer>
                      </SelfSerivceCatalogIconWrapper>
                      <SelfSerivceCatalogTitle>
                        {catalogItem.title}
                      </SelfSerivceCatalogTitle>
                    </SelfSerivceCatalogTop>
                    <SelfSerivceCatalogDown>
                      <SelfSerivceCatalogDescription>
                        {catalogItem.description}
                      </SelfSerivceCatalogDescription>
                      <ul
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          listStyle: "none",
                          padding: 0,
                        }}
                      >
                        {catalogItem.content.map((contentInfo, key) => (
                          <li key={key + 1}>
                            <SelfSerivceCatalogPreRequisiteTitle>
                              {contentInfo.title}
                            </SelfSerivceCatalogPreRequisiteTitle>
                            <SelfSerivceCatalogPreRequisites>
                              {contentInfo.contentItems.map(
                                (contentItem, contentItemIndex) => (
                                  <SelfSerivceCatalogPreRequisite
                                    key={contentItemIndex + 1}
                                  >
                                    {contentItem}
                                  </SelfSerivceCatalogPreRequisite>
                                )
                              )}
                            </SelfSerivceCatalogPreRequisites>
                          </li>
                        ))}
                      </ul>
                      <SelfServiceCatalogFooter>
                        {catalogItem.docsRoute ? (
                          <SelfServiceCatalogFooterLeft>
                            { CLOUD_SERVICE_PAGE_CONSTANTS.toLearnMore }{" "}
                            <SelfServiceCatalogFooterLink
                              to={catalogItem.docsRoute as string}
                            >
                              { CLOUD_SERVICE_PAGE_CONSTANTS.readDocs }
                            </SelfServiceCatalogFooterLink>
                          </SelfServiceCatalogFooterLeft>
                        ) : (
                          <div></div>
                        )}
                        <Link
                          to={{
                            pathname: getPathnameForCatalogItem(
                              catalogGroup.title,
                              catalogItem.selectEndpoint
                            ),
                          }}
                          style={{ textDecoration: "none" }}
                        >
                          <PrimaryButton>
                            <img
                              width={12.75}
                              height={17}
                              alt="arrow-icon"
                              src="/assets/cursor.svg"
                            />
                            <span>{ CLOUD_SERVICE_PAGE_CONSTANTS.select }</span>
                          </PrimaryButton>
                        </Link>
                      </SelfServiceCatalogFooter>
                    </SelfSerivceCatalogDown>
                  </SelfSerivceCatalogGroupItem>
                );
              })}
            </SelfSerivceCatalogGroupItems>
          </SelfSerivceCatalogGroup>
        ))}
      </SelfSerivceCatalogWrapper>
    </PageWrapper>
  );
}

export default SelfServicePortal;
